import { createRoot } from 'react-dom/client';
import { createElement } from 'react';
import { FixtureRegistry, type FixtureExport, type FixtureModules, type ExplorerRuntime } from '../core/index.js';
import { Explorer } from '../components/index.js';
import { DaemonModel } from '../daemon/index.js';
import type { DaemonConfig } from '../daemon/DaemonConfig.js';
import { ReportModel } from '../report/index.js';
import { setExplorerShadowHost } from '../lib/explorerShadowHost.js';

import explorerCss from '../styles.css?inline';
import darkThemeCss from '../vscode-styles/default-dark-plus.css?inline';
import lightThemeCss from '../vscode-styles/default-light-plus.css?inline';

export class ExplorerUIMode implements ExplorerRuntime {
	private readonly _root: ReturnType<typeof createRoot>;
	private readonly _registry = new FixtureRegistry();
	private readonly _daemonModel?: DaemonModel;
	private readonly _reportModel?: ReportModel;

	constructor(element: HTMLElement, loadFixtures: Promise<FixtureModules>, daemonConfig?: DaemonConfig) {
		this._registry.loading.set(true, undefined);
		loadFixtures.then(
			modules => {
				this._populateRegistry(modules);
				this._registry.loading.set(false, undefined);
				this._registry.error.set(undefined, undefined);
			},
			error => {
				const message = error instanceof Error ? (error.stack ?? error.message) : String(error);
				this._registry.loading.set(false, undefined);
				this._registry.error.set(message, undefined);
			},
		);
		this._reportModel = this._initializeReportModel();
		this._daemonModel = this._reportModel ? undefined : this._initializeDaemonModel(daemonConfig);

		const shadow = element.attachShadow({ mode: 'open' });
		const sheet = new CSSStyleSheet();
		sheet.replaceSync([explorerCss, darkThemeCss, lightThemeCss].join('\n'));
		shadow.adoptedStyleSheets = [sheet];

		setExplorerShadowHost(element);

		const uiContainer = document.createElement('div');
		uiContainer.style.height = '100%';
		uiContainer.style.width = '100%';
		shadow.appendChild(uiContainer);

		this._root = createRoot(uiContainer);
		this._root.render(createElement(Explorer, {
			registry: this._registry,
			daemonModel: this._daemonModel,
			reportModel: this._reportModel,
		}));
	}

	private _initializeReportModel(): ReportModel | undefined {
		const params = new URLSearchParams(location.search);
		const reportUrl = params.get('report');
		if (!reportUrl) return undefined;
		return new ReportModel(reportUrl);
	}

	private _initializeDaemonModel(daemonConfig: DaemonConfig | undefined): DaemonModel | undefined {
		if (!daemonConfig) {
			return undefined;
		}
		const model = new DaemonModel(daemonConfig);
		model.initialize().catch(err => {
			console.error('[ExplorerUIMode] Failed to initialize DaemonModel:', err);
		});
		return model;
	}

	private _populateRegistry(fixtureModules: FixtureModules): void {
		this._registry.clear();
		for (const [path, module] of Object.entries(fixtureModules)) {
			const fixture = module.default;
			if (fixture && typeof fixture === 'object') {
				this._registry.register(path, fixture as FixtureExport);
			}
		}
	}

	updateFixtures(fixtureModules: FixtureModules): void {
		this._populateRegistry(fixtureModules);
		this._registry.loading.set(false, undefined);
		this._registry.error.set(undefined, undefined);
	}

	bumpVersion(): void {}

	dispose(): void {
		this._daemonModel?.dispose();
		this._root.unmount();
	}
}
